import React, { useEffect, useState } from "react";
import {
  MinusIcon,
  PhotoIcon,
  PlusIcon,
  UserCircleIcon,
} from "@heroicons/react/24/solid";
import { ChevronDownIcon } from "@heroicons/react/16/solid";
import { useSelector } from "react-redux";
import { editPlan, getPermsList } from "../services/PlanServices";
import { confirmPlanModelsToast, showToast } from "toast/toastHelper";
import { Disclosure, Field, Label, Switch } from "@headlessui/react";
import { PulseLoader } from "react-spinners";
import { useNavigate } from "react-router-dom";
import StatusSwitch from "./BotSelect";
import MultiSelectReactSelect from "./MultiSelectReactSelect";
import { botList } from "units/admin/services/adminServices";
export const PermCategory = {
  PAGE: "page",
  API: "api",
  AI_MODEL: "ai",
  EMBEDDER: "textEmbder",
  CHART: "analysticChart",
  PLATFORM: "platform",
  TOOL: "ai-tool",
};

const buildOptionsByCategory = (data, category) =>
  data
    .filter((item) => item.category === category && item.isAvailable)
    .map((item) => ({
      value: item.id,
      label: item.persianName,
      name: item.name,
    }));
export default function EditPlanForm({ selectedPlan }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(selectedPlan);
  // MultiSelect fields (Array)
  const [selectedOptions, setSelectedOptions] = useState({
    charts: [],
    languageModels: [],
    embeddingModels: [],
    tools: [],
    pageAccess: [],
    socialPlatform: [],
  });
  const [chartsOptions, setChartsOptions] = useState([]);
  const [languageModelsOptions, setLanguageModelsOptions] = useState([]);
  const [embeddingModelsOptions, setEmbeddingModelsOptions] = useState([]);
  const [toolsOptions, setToolsOptions] = useState([]);
  const [socialPlatformOptions, setSocialPlatformOptions] = useState([]);
  const [pageAccessOptions, setPageAccessOptions] = useState([]);
  const [botsEnabled, setBotsEnabled] = useState(
    Array.isArray(selectedPlan?.bots) && selectedPlan.bots.length > 0,
  );

  const [botsOptions, setBotsOptions] = useState([]);
  const [selectedBots, setSelectedBots] = useState(
    selectedPlan?.bots?.map((e) => ({
      value: e.id,
      label: e.username,
    })),
  );
  const [loadingBots, setLoadingBots] = useState(false);
  const isOptionsLoading = !languageModelsOptions.length;
  const selectOptionsMap = {
    charts: chartsOptions,
    languageModels: languageModelsOptions,
    embeddingModels: embeddingModelsOptions,
    tools: toolsOptions,
    socialPlatform: socialPlatformOptions,
    pageAccess: pageAccessOptions,
  };
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const accessToken = useSelector((state) => state.auth.accessToken);
  const toEnglishDigits = (str) => {
    if (typeof str !== "string") str = String(str);
    return str
      .replace(/[\u200E\u200F]/g, "") // حذف کاراکترهای نامرئی RTL و LTR
      .replace(/[٬,،\s]/g, "") // حذف تمام کاماها و فاصله‌ها
      .replace(/[۰-۹]/g, (d) =>
        String.fromCharCode(d.charCodeAt(0) - 0x06f0 + 48),
      )
      .replace(/[٠-٩]/g, (d) =>
        String.fromCharCode(d.charCodeAt(0) - 0x0660 + 48),
      )
      .replace(/−/g, "-");
  };

  const parseNumber = (val) => {
    const num = Number(toEnglishDigits(val));
    return isNaN(num) ? 0 : num;
  };

  const formatToPersianComma = (value) => {
    const plain = toEnglishDigits(value).trim();
    if (plain === "") return "";
    const num = Number(plain);
    if (isNaN(num)) return value;
    return num.toLocaleString("fa-IR");
  };

  const cleanNumberField = (value) => {
    if (typeof value !== "string") return value;

    // تبدیل ارقام فارسی و عربی به انگلیسی
    const englishDigits = value
      .replace(/[۰-۹]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 1728))
      .replace(/[٠-٩]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 1632));

    // حذف کاما و تبدیل به عدد
    return Number(englishDigits.replaceAll(",", ""));
  };
  const fieldLimits = {
    ragSlots: {
      min: 5,
      max: 15,
      message: "تعداد رگ باید بین ۵ تا ۱۵ باشد.",
    },
    ragTokens: {
      min: 4000,
      max: 12000,
      message: "تعداد توکن رگ باید بین ۴۰۰۰ تا ۱۲٬۰۰۰ باشد.",
    },
  };
  const handleChange = (e) => {
    // const { name, value } = e.target;
    const { name, value, type, checked } = e.target;
    // ولیدیشن ragSlots و ragTokens
    if (name === "ragSlots" || name === "ragTokens") {
      const plainValue = toEnglishDigits(value).trim();

      // اجازه خالی کردن input هنگام تایپ
      if (plainValue === "") {
        setFormData((prev) => ({
          ...prev,
          [name]: "",
        }));

        setErrors((prev) => ({
          ...prev,
          [name]: false,
        }));

        return;
      }

      // فقط عدد مثبت
      if (!/^\d+$/.test(plainValue)) {
        return;
      }

      const num = Number(plainValue);
      const { min, max } = fieldLimits[name];

      // مقدار را نمایش بده حتی اگر خارج از محدوده باشد
      setFormData((prev) => ({
        ...prev,
        [name]: formatToPersianComma(plainValue),
      }));

      // نمایش خطا
      if (num < min || num > max) {
        setErrors((prev) => ({
          ...prev,
          [name]: true,
        }));
      } else {
        setErrors((prev) => ({
          ...prev,
          [name]: false,
        }));
      }

      return;
    }
    // --- حل مشکل چک‌باکس isFeatured و هر چک‌باکس دیگری ---

    const allowedNegativeOneFields = [
      "duration",
      "messagePerDay",
      "charLimit",
      "fileSize",
      "files",
    ];
    if (type === "checkbox") {
      setFormData((prev) => ({ ...prev, [name]: checked }));
      return;
    }
    if (name === "offPercentage" || name === "platformPercent") {
      const englishValue = toEnglishDigits(value).trim();
      const num = Number(englishValue);

      if (!isNaN(num) && num >= 0 && num <= 100) {
        setFormData((prev) => ({
          ...prev,
          [name]: formatToPersianComma(englishValue),
        }));
      } else if (value === "") {
        setFormData((prev) => ({ ...prev, [name]: "" }));
      }
      return;
    }
    if (name === "offPercentage") {
      const englishValue = toEnglishDigits(value).trim();
      const num = Number(englishValue);

      if (!isNaN(num) && num >= 0 && num <= 100) {
        setFormData((prev) => ({
          ...prev,
          [name]: formatToPersianComma(englishValue),
        }));
      } else if (value === "") {
        setFormData((prev) => ({ ...prev, [name]: "" }));
      }
      return;
    }

    const plainValue = toEnglishDigits(value).trim();
    // اجازه بده کاربر "-" خالی وارد کنه برای ادامه‌ی تایپ -1
    if (plainValue === "-") {
      setFormData((prev) => ({ ...prev, [name]: "-" }));
      return;
    }
    // اگر -1 بود و فیلد مجازه
    if (plainValue === "-1") {
      if (allowedNegativeOneFields.includes(name)) {
        const formatted = formatToPersianComma("-1");
        setFormData((prev) => ({ ...prev, [name]: formatted }));
      }
      return;
    }
    // اگر مقدار عددی معتبر بود
    const num = Number(plainValue);
    if (!isNaN(num) && num >= 0) {
      const formatted = formatToPersianComma(plainValue);
      setFormData((prev) => ({ ...prev, [name]: formatted }));
      return;
    }
    let normalizedValue;

    if (name === "name") {
      normalizedValue = value.toLowerCase();
    } else if (name === "isActive") {
      normalizedValue = value === "true";
    } else if (plainValue === "-1") {
      // فقط اگر فیلد مجازه -1 بگیره
      if (allowedNegativeOneFields.includes(name)) {
        normalizedValue = formatToPersianComma("-1");
      } else {
        return; // در غیر اینصورت رد کن
      }
    } else {
      normalizedValue = formatToPersianComma(plainValue);
    }
    const offRegex = /^(?:100|[۱-۹]?[۰-۹]|[1-9]?[0-9])$/;

    setFormData((prev) => ({ ...prev, [name]: normalizedValue }));
  };
  // multi select box
  useEffect(() => {
    const fetchPlanOptions = async () => {
      try {
        const res = await getPermsList(accessToken);
        if (res?.ok) {
          const data = res?.data;
          setChartsOptions(buildOptionsByCategory(data, PermCategory.CHART));
          setLanguageModelsOptions(
            buildOptionsByCategory(data, PermCategory.AI_MODEL),
          );
          setEmbeddingModelsOptions(
            buildOptionsByCategory(data, PermCategory.EMBEDDER),
          );
          setToolsOptions(buildOptionsByCategory(data, PermCategory.TOOL));
          setSocialPlatformOptions(
            buildOptionsByCategory(data, PermCategory.PLATFORM),
          );
          setPageAccessOptions(buildOptionsByCategory(data, PermCategory.PAGE));
        }
      } catch (e) {
        console.error(e);
      }
    };

    fetchPlanOptions();
  }, []);
  // const mapIdsToOptionsByCategory = (permissions = [], options = [], category) => {
  //     if (!Array.isArray(permissions) || !Array.isArray(options)) return [];

  //     const idsForCategory = permissions
  //         .filter(p => p.category === category)
  //         .map(p => p.id);

  //     return options.filter(opt => idsForCategory.includes(opt.value));
  // };
  const mapIdsToOptionsByCategory = (permissions = [], options = []) => {
    const permissionNames = permissions.map((p) => p.name);
    return options.filter((opt) => permissionNames.includes(opt.name));
  };

  useEffect(() => {
    if (!selectedPlan || !selectedPlan.permissions) return;

    // setSelectedOptions({
    //     charts: mapIdsToOptionsByCategory(selectedPlan.permissions, chartsOptions, PermCategory.CHART),
    //     languageModels: mapIdsToOptionsByCategory(selectedPlan.permissions, languageModelsOptions, PermCategory.AI_MODEL),
    //     embeddingModels: mapIdsToOptionsByCategory(selectedPlan.permissions, embeddingModelsOptions, PermCategory.EMBEDDER),
    //     tools: mapIdsToOptionsByCategory(selectedPlan.permissions, toolsOptions, PermCategory.TOOL),
    //     pageAccess: mapIdsToOptionsByCategory(selectedPlan.permissions, pageAccessOptions, PermCategory.PAGE),
    //     socialPlatform: mapIdsToOptionsByCategory(selectedPlan.permissions, socialPlatformOptions, PermCategory.PLATFORM),
    // });
    setSelectedOptions({
      charts: mapIdsToOptionsByCategory(
        selectedPlan.permissions,
        chartsOptions,
      ),
      languageModels: mapIdsToOptionsByCategory(
        selectedPlan.permissions,
        languageModelsOptions,
      ),
      embeddingModels: mapIdsToOptionsByCategory(
        selectedPlan.permissions,
        embeddingModelsOptions,
      ),
      tools: mapIdsToOptionsByCategory(selectedPlan.permissions, toolsOptions),
      pageAccess: mapIdsToOptionsByCategory(
        selectedPlan.permissions,
        pageAccessOptions,
      ),
      socialPlatform: mapIdsToOptionsByCategory(
        selectedPlan.permissions,
        socialPlatformOptions,
      ),
    });
  }, [
    selectedPlan,
    chartsOptions,
    languageModelsOptions,
    embeddingModelsOptions,
    toolsOptions,
    pageAccessOptions,
    socialPlatformOptions,
  ]);
  const handleSelectBoxChange = (e) => {
    const { name, value } = e.target;

    setSelectedOptions((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // bot list api
  useEffect(() => {
    if (!botsEnabled) {
      setSelectedBots([]);
      return;
    }

    const fetchBots = async () => {
      setLoadingBots(true);
      try {
        const res = await botList(accessToken);
        setBotsOptions(
          res?.data?.map((bot) => ({
            value: bot.id,
            label: bot.username,
          })),
        );
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingBots(false);
      }
    };

    fetchBots();
  }, [botsEnabled]);
  const mapSelectToValues = (arr = []) => arr.map((item) => item.value);
  const parseField = (key, value) => {
    const minusOneAllowed = [
      "duration",
      "messagePerDay",
      "charLimit",
      "fileSize",
      "files",
    ];

    if (value === null || value === undefined || value === "") {
      return 0;
    }

    const plain = toEnglishDigits(value).trim();

    // -1 فقط برای فیلدهای مجاز
    if (plain === "-1" && minusOneAllowed.includes(key)) {
      return -1;
    }

    const num = Number(plain);

    if (isNaN(num) || num < 0) {
      throw new Error(`مقدار نامعتبر برای ${key}`);
    }

    // Validation ragSlots
    if (key === "ragSlots" && (num < 5 || num > 15)) {
      throw new Error("تعداد ragSlots باید بین 5 تا 15 باشد");
    }

    // Validation ragTokens
    if (key === "ragTokens" && (num < 4000 || num > 12000)) {
      throw new Error("تعداد ragTokens باید بین 4000 تا 12000 باشد");
    }

    return num;
  };
  const submitEditPlan = async (permissions) => {
    try {
      const platformPercent = parseField("platformPercent", formData?.platformPercent);
      const res = await editPlan({
        name: formData?.name,
        id: formData?.id,
        active: formData?.isActive,
        price: Number(toEnglishDigits(formData?.price)),
        days: parseField("duration", formData?.duration),
        tokens: parseField("tokens", formData?.tokens),
        files: parseField("files", formData?.files),
        fileSize: parseField("fileSize", formData?.fileSize),
        userLimit: parseField("userLimit", formData?.userLimit),
        messagePerDay: parseField("messagePerDay", formData?.messagePerDay),
        promptTokens: parseField("promptTokens", formData?.promptTokens),
        userTokens: parseField("userTokens", formData?.userTokens),
        charLimit: parseField("charLimit", formData?.charLimit),
        fileTokens: parseField("fileTokens", formData?.fileTokens),
        gptMaxTokens: parseField("gptMaxTokens", formData?.gptMaxTokens),
        off: parseField("offPercentage", formData?.offPercentage),
        ragSlots: Number(toEnglishDigits(formData?.ragSlots)),
        ragTokens: Number(toEnglishDigits(formData?.ragTokens)),
        platformPercent: platformPercent,
        isFeatured: formData?.isFeatured,
        permissions,
        bots: botsEnabled ? mapSelectToValues(selectedBots) : null,
        accessToken,
      });

      if (res?.ok) {
        showToast("طرح با موفقیت ویرایش شد.", "success");
        navigate("/admin/plans");
      }
    } catch (error) {
      showToast(
        error.message || "خطا در ارسال اطلاعات. لطفاً مقادیر وارد شده را بررسی کنید.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    const ignoreFields = ["permissions", "bots"];
    const requiredFields = Object.entries(formData).filter(
      ([key, val]) =>
        !ignoreFields.includes(key) &&
        key !== "offPercentage" &&
        (val === "" || val === null || val === undefined),
    );
    const rangeErrors = {};

    ["ragSlots", "ragTokens"].forEach((key) => {
      const value = parseNumber(formData?.[key]);
      const { min, max } = fieldLimits[key];

      if (value < min || value > max) {
        rangeErrors[key] = true;
      }
    });

    if (Object.keys(rangeErrors).length > 0) {
      setErrors((prev) => ({
        ...prev,
        ...rangeErrors,
      }));

      return;
    }

    if (requiredFields.length > 0) {
      const newErrors = {};
      requiredFields.forEach(([key]) => {
        newErrors[key] = true;
      });
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    setErrors({});

    // فیلدهایی که مجاز به دریافت -1 هستن
    // const parseField = (key, value) => {
    //     const minusOneAllowed = [
    //         "duration",
    //         "messagePerDay",
    //         "charLimit",
    //         "fileSize",
    //         "files"
    //     ];

    //     const plain = toEnglishDigits(value).trim();

    //     if (plain === "-1" && minusOneAllowed.includes(key)) {
    //         return -1;
    //     }

    //     const num = Number(plain);
    //     if (!isNaN(num) && num >= 0) {
    //         return num;
    //     }

    //     throw new Error(`مقدار نامعتبر برای فیلد ${key}`);
    // };

    // تابع کمکی برای تبدیل مقدار فیلد
    // const parseField = (key, value) => {
    //     const num = Number(toEnglishDigits(value));
    //     if (minusOneAllowed.includes(key)) {
    //         if (num === -1 || num >= 0) return num;
    //     } else {
    //         if (num >= 0) return num;
    //     }
    //     throw new Error(`مقدار نامعتبر برای فیلد ${key}`);
    // };
    // const permissionsArray = [
    //     ...mapSelectToValues(selectedOptions.charts),
    //     ...mapSelectToValues(selectedOptions.languageModels),
    //     ...mapSelectToValues(selectedOptions.embeddingModels),
    //     ...mapSelectToValues(selectedOptions.tools),
    //     ...mapSelectToValues(selectedOptions.pageAccess),
    //     ...mapSelectToValues(selectedOptions.socialPlatform),
    // ];
    // const permissions = Array.from(new Set([
    //     ...permissionsArray,
    //     ...selectedPlan.permissions.map(p => p.id),
    // ]));

    // تبدیل selectedOptions به آرایه id


    const permissionsArray = [
      ...mapSelectToValues(selectedOptions.charts),
      ...mapSelectToValues(selectedOptions.languageModels),
      ...mapSelectToValues(selectedOptions.embeddingModels),
      ...mapSelectToValues(selectedOptions.tools),
      ...mapSelectToValues(selectedOptions.pageAccess),
      ...mapSelectToValues(selectedOptions.socialPlatform),
    ];

    // اگر آرایه خالی بود، null بفرست
    const permissions =
      permissionsArray.length > 0
        ? Array.from(new Set(permissionsArray))
        : null;
    // همین کار برای bots
    const bots = botsEnabled
      ? selectedBots.length > 0
        ? mapSelectToValues(selectedBots)
        : null
      : null;
    if (
      selectedOptions.languageModels.length === 0 ||
      selectedOptions.embeddingModels.length === 0
    ) {
      setLoading(false);

      return confirmPlanModelsToast(() => {
        setLoading(true);
        submitEditPlan(permissions);
      });
    }
    await submitEditPlan(permissions);
  }
  return (
    <form onSubmit={handleSubmit}>
      <div className="space-y-12 ">
        <div className=" pb-5">
          <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
            <div className="sm:col-span-3">
              <label
                htmlFor="name"
                className="block text-sm/6 font-medium text-gray-900"
              >
                نام طرح
              </label>
              <div className="mt-2">
                <input
                  id="name"
                  name="name"
                  value={formData?.name}
                  placeholder="رایاپک..."
                  type="text"
                  onChange={handleChange}
                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 
                                    -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-[#aadec5] sm:text-sm/6"
                />
              </div>
              {errors.name && (
                <p className="text-red-500 text-sm">این فیلد الزامی است</p>
              )}
            </div>
            <div className="sm:col-span-3">
              <StatusSwitch value={botsEnabled} onChange={setBotsEnabled} />

              <div
                className={`transition-all duration-300 ease-in-out
                                   ${botsEnabled
                    ? "opacity-100 translate-y-0 mt-4 pointer-events-auto"
                    : "opacity-0 -translate-y-2 pointer-events-none"
                  }`}
              >
                {botsEnabled && (
                  <div className=" ">
                    <label className="mb-2 block text-sm text-gray-700">
                      لیست بات ها
                    </label>

                    <MultiSelectReactSelect
                      name="bots"
                      value={selectedBots}
                      options={botsOptions}
                      onChange={(e) => setSelectedBots(e.target.value)}
                      isDisabled={loadingBots}
                      className="w-full"
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="sm:col-span-3">
              <label
                htmlFor="tokens"
                className="block text-sm/6 font-medium text-gray-900"
              >
                تعداد توکن های اختصاص داده شده به طرح
              </label>
              <div className="mt-2">
                <input
                  id="tokens"
                  name="tokens"
                  value={formData?.tokens.toLocaleString("fa-IR")}
                  type="text"
                  onChange={handleChange}
                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 border border-gray-300 placeholder:text-gray-400 
                                      focus:outline-none focus:border-[#aadec5] focus:ring-2 focus:ring-[#aadec5] sm:text-sm/6"
                />
              </div>
              {errors.tokens && (
                <p className="text-red-500 text-sm">این فیلد الزامی است</p>
              )}
            </div>

            <div className="sm:col-span-3">
              <label
                htmlFor="files"
                className="block text-sm/6 font-medium text-gray-900"
              >
                تعداد فایل مجاز برای آپلود
              </label>
              <div className="mt-2">
                <input
                  id="files"
                  name="files"
                  value={formData?.files.toLocaleString("fa-IR")}
                  type="text"
                  onChange={handleChange}
                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 
                                    -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-none focus:border-[#aadec5] focus:ring-2 focus:ring-[#aadec5] sm:text-sm/6"
                />
              </div>
              <small className="mt-1 text-gray-600">
                *برای تعداد فایل نامحدود، مقدار -1 را وارد کنید.
              </small>
              <br />
              {errors.files && (
                <p className="text-red-500 text-sm">این فیلد الزامی است</p>
              )}
            </div>
            <div className="sm:col-span-3">
              <label
                htmlFor="fileSize"
                className="block text-sm/6 font-medium text-gray-900"
              >
                حجم مجاز هر فایل بارگذاری شده
              </label>
              <div className="mt-2">
                <input
                  id="fileSize"
                  name="fileSize"
                  value={formData?.fileSize.toLocaleString("fa-IR")}
                  type="text"
                  onChange={handleChange}
                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-none focus:border-[#aadec5] focus:ring-2 focus:ring-[#aadec5] sm:text-sm/6"
                />
              </div>
              <small className="mt-1 text-gray-600">
                * حجم هر فایل برحسب مگابایت می باشد(برای حجم نامحدود، مقدار -1
                را وارد کنید.).
              </small>
              <br />
              {errors.fileSize && (
                <p className="text-red-500 text-sm">این فیلد الزامی است</p>
              )}
            </div>
            <div className="sm:col-span-3">
              <label
                htmlFor="userLimit"
                className="block text-sm/6 font-medium text-gray-900"
              >
                تعداد کاربر
              </label>
              <div className="mt-2">
                <input
                  id="userLimit"
                  name="userLimit"
                  value={formData?.userLimit.toLocaleString("fa-IR")}
                  type="text"
                  onChange={handleChange}
                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-none focus:border-[#aadec5] focus:ring-2 focus:ring-[#aadec5] sm:text-sm/6"
                />
              </div>
              {errors.userLimit && (
                <p className="text-red-500 text-sm">این فیلد الزامی است</p>
              )}
            </div>
            <div className="sm:col-span-3">
              <label
                htmlFor="price"
                className="block text-sm/6 font-medium text-gray-900"
              >
                قیمت طرح
              </label>
              <div className="mt-2">
                <input
                  id="price"
                  name="price"
                  value={formData?.price.toLocaleString("fa-IR")}
                  type="text"
                  onChange={handleChange}
                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-none focus:border-[#aadec5] focus:ring-2 focus:ring-[#aadec5] sm:text-sm/6"
                />
              </div>
              <small className="mt-1 text-gray-600">
                * قیمت طرح به ریال می باشد.
              </small>
              <br />
              {errors.price && (
                <p className="text-red-500 text-sm">این فیلد الزامی است</p>
              )}
            </div>

            <div className="sm:col-span-3">
              <label
                htmlFor="duration"
                className="block text-sm/6 font-medium text-gray-900"
              >
                مدت زمان اعتبار طرح
              </label>
              <div className="mt-2">
                <input
                  id="duration"
                  name="duration"
                  value={formData?.duration.toLocaleString("fa-IR")}
                  type="text"
                  onChange={handleChange}
                  placeholder="...روز"
                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-none focus:border-[#aadec5] focus:ring-2 focus:ring-[#aadec5] sm:text-sm/6"
                />
              </div>
              <small className="mt-1 text-gray-600">
                *برای روزهای نامحدود، مقدار -1 را وارد کنید.
              </small>
              <br />
              {errors.duration && (
                <p className="text-red-500 text-sm">این فیلد الزامی است</p>
              )}
            </div>
            <div className="sm:col-span-3">
              <label
                htmlFor="offPercentage"
                className="block text-sm/6 font-medium text-gray-900"
              >
                تخفیف
              </label>
              <div className="mt-2">
                <input
                  id="offPercentage"
                  name="offPercentage"
                  // value={formData?.offPercentage}
                  value={
                    formData?.offPercentage !== null &&
                      formData?.offPercentage !== undefined
                      ? formData?.offPercentage
                      : 0
                  }
                  type="text"
                  onChange={handleChange}
                  placeholder=""
                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-none focus:border-[#aadec5] focus:ring-2 focus:ring-[#aadec5] sm:text-sm/6"
                />
              </div>
              <small className="mt-1 text-gray-600">
                *میزان تخفیف مورد نظر را وارد کنید.
              </small>
              <label
                htmlFor="platformPercent"
                className="block text-sm/6 font-medium text-gray-900"
              >
                درصد کارمزد پلتفرم
              </label>
              <div className="mt-2">
                <input
                  id="platformPercent"
                  name="platformPercent"
                  value={
                    formData?.platformPercent !== null &&
                      formData?.platformPercent !== undefined
                      ? formData?.platformPercent
                      : 0
                  }
                  type="text"
                  onChange={handleChange}
                  placeholder="مثلاً: 10"
                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-none focus:border-[#aadec5] focus:ring-2 focus:ring-[#aadec5] sm:text-sm/6"
                />
              </div>
              <small className="mt-1 text-gray-600">
                *درصد پلتفرم از درآمد مدیر کسب و کار از پرداختی های مشتری را وارد کنید. (بین 0 تا 100)
              </small>
            </div>
            <div className="sm:col-span-3">
              <fieldset className="flex flex-row items-center gap-6">
                <label className="shrink-0 text-sm font-semibold text-gray-900 whitespace-nowrap">
                  {" "}
                  وضعیت طرح
                </label>
                {/* <p className="mt-1 text-sm/6 text-gray-600">These are delivered via SMS to your mobile phone.</p> */}
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    <input
                      id="true-Status"
                      name="isActive"
                      type="radio"
                      value="true"
                      checked={formData?.isActive == true}
                      disabled={formData?.isBase}
                      onChange={handleChange}
                      className={`size-4  rounded-full border border-gray-300
                                            ${formData?.isActive === true ? "checked:bg-green-500 " : "checked:bg-gray-300 checked:border-gray-300"}
                                            focus:ring-2 focus:ring-green-500`}
                    />
                    <label
                      htmlFor="true-Status"
                      className="inline-block text-sm/6 font-medium text-gray-900"
                    >
                      فعال
                    </label>
                  </div>
                  <div className="flex items-center gap-x-3">
                    <input
                      id="false-status"
                      name="isActive"
                      type="radio"
                      value="false"
                      checked={formData?.isActive == false}
                      disabled={formData?.isBase}
                      onChange={handleChange}
                      className={`size-4  rounded-full border border-gray-300 
                                                   ${formData?.isActive === false ? "checked:bg-red-500 checked:border-red-500" : "checked:bg-gray-300 checked:border-gray-300"}
                                                focus:ring-2 focus:ring-red-500`}
                    />
                    <label
                      htmlFor="false-status"
                      className="inline-block text-sm/6 font-medium text-gray-900"
                    >
                      غیرفعال
                    </label>
                  </div>
                </div>
              </fieldset>
            </div>
            <div className="mt-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  id="isFeatured"
                  name="isFeatured"
                  checked={formData?.isFeatured}
                  onChange={handleChange}
                  className="
            size-4 cursor-pointer rounded
            border border-gray-300
            checked:hover:border-transparent
         checked:hover:bg-green-600
         checked:focus:border-green-600
         checked:focus:bg-green-600
            accent-green-600
            checked:bg-green-600 checked:border-green-600

            focus:outline-none
            focus:ring-2 focus:ring-green-300
            focus:ring-offset-0
            focus-visible:ring-green-300

            active:ring-0 active:outline-none active:bg-green-600
            hover:ring-0

            transition-all duration-150
        "
                />
                <span className="text-sm font-medium text-gray-900">
                  طرح برگزیده
                </span>
              </label>
            </div>
          </div>
        </div>
        <div className="sm:col-span-3">
          <Disclosure as="div" className="border-t border-gray-200 px-4 py-6">
            {({ open }) => (
              <>
                <h3 className="-mx-2 -my-3 flow-root">
                  <Disclosure.Button className="group flex w-full items-center justify-between bg-white px-2 py-3 text-gray-400 hover:text-gray-500">
                    <span className="font-medium text-gray-900">
                      تنظیمات پیشرفته
                    </span>
                    <span className="ml-6 flex items-center">
                      {open ? (
                        <MinusIcon
                          aria-hidden="true"
                          className="size-5 text-gray-400"
                        />
                      ) : (
                        <PlusIcon
                          aria-hidden="true"
                          className="size-5 text-gray-400"
                        />
                      )}
                    </span>
                  </Disclosure.Button>
                </h3>

                <Disclosure.Panel className="pt-6">
                  <div className="space-y-6">
                    {[
                      {
                        label: "حداکثر توکن ارسالی  از کاربر (دریافتی GPT)",
                        name: "userTokens",
                        value: formData?.userTokens.toLocaleString("fa-IR"),
                      },
                      {
                        label: "حداکثر توکن دریافتی  به کاربر (ارسالی GPT)",
                        name: "gptMaxTokens",
                        value: formData?.gptMaxTokens.toLocaleString("fa-IR"),
                      },
                      {
                        label: " حداکثر کاراکتر مصرفی مجاز",
                        name: "charLimit",
                        value: formData?.charLimit.toLocaleString("fa-IR"),
                      },
                      {
                        label: "تعداد پیام روزانه مجاز(هر ارسال - دریافت)",
                        name: "messagePerDay",
                        value: formData?.messagePerDay.toLocaleString("fa-IR"),
                      },
                      {
                        label: "حداکثر توکن مجاز پرامپت سیستمی ",
                        name: "promptTokens",
                        value: formData?.promptTokens.toLocaleString("fa-IR"),
                      },
                      {
                        label: "حداکثر توکن مجاز برای بارگذاری هر فایل",
                        name: "fileTokens",
                        value: formData?.fileTokens.toLocaleString("fa-IR"),
                      },
                      {
                        label: "تعداد رگ های جمع آوری اطلاعات به هوش مصنوعی",
                        name: "ragSlots",
                        value: formData?.ragSlots.toLocaleString("fa-IR"),
                      },
                      {
                        label: "تعداد توکن های مجاز مصرفی رگ برای ارسال اطلاعات به هوش مصنوعی",
                        name: "ragTokens",
                        value: formData?.ragTokens.toLocaleString("fa-IR"),
                      },
                    ].map((item) => (
                      <div
                        key={item.name}
                        className="flex flex-row items-center justify-between gap-2"
                      >
                        <div className="flex flex-col  justify-start items-start ">
                          <label
                            htmlFor={item.name}
                            className="text-gray-600 text-sm "
                          >
                            {item.label}
                          </label>
                          {"messagePerDay".includes(item.name) && (
                            <small className="mt-1 text-gray-600">
                              *برای پیام روزانه ی نامحدود، مقدار -1 را وارد
                              کنید.
                            </small>
                          )}
                          {"charLimit".includes(item.name) && (
                            <small className="mt-1 text-gray-600">
                              *برای کاراکتر مصرفی نامحدود، مقدار -1 را وارد
                              کنید.
                            </small>
                          )}
                        </div>
                        <div className="w-1/4">
                          <input
                            id={item.name}
                            name={item.name}
                            type="text"
                            value={item.value}
                            onChange={handleChange}
                            className={`w-full rounded-md bg-white px-4 py-2 text-gray-900 text-sm outline outline-1 ${errors[item.name]
                              ? "outline-red-500 focus:border-red-500 focus:ring-red-200"
                              : "outline-gray-300 focus:border-[#aadec5] focus:ring-[#aadec5]"
                              } placeholder:text-gray-400 focus:outline-none focus:ring-2`}
                          />

                          {errors[item.name] && fieldLimits[item.name] && (
                            <p className="mt-1 text-xs text-red-500">
                              {fieldLimits[item.name].message}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </Disclosure.Panel>
              </>
            )}
          </Disclosure>
        </div>
        <div className="sm:col-span-3">
          <Disclosure as="div" className="border-t border-gray-200 px-4 py-6">
            {({ open }) => (
              <>
                <h3 className="-mx-2 -my-3 flow-root">
                  <Disclosure.Button className="group flex w-full items-center justify-between bg-white px-2 py-3 text-gray-400 hover:text-gray-500">
                    <span className="font-medium text-gray-900">
                      امکانات رایابات{" "}
                    </span>
                    <span className="ml-6 flex items-center">
                      {open ? (
                        <MinusIcon
                          aria-hidden="true"
                          className="size-5 text-gray-400"
                        />
                      ) : (
                        <PlusIcon
                          aria-hidden="true"
                          className="size-5 text-gray-400"
                        />
                      )}
                    </span>
                  </Disclosure.Button>
                </h3>
                <Disclosure.Panel className="pt-6">
                  <div className="space-y-6">
                    {[
                      {
                        label: "پلتفرم شبکه های اجتماعی",
                        name: "socialPlatform",
                      },
                      { label: "نمودارها", name: "charts" },
                      { label: "مدل های زبانی", name: "languageModels" },
                      { label: "مدل های برداری", name: "embeddingModels" },
                      { label: "ابزارها", name: "tools" },
                      { label: "دسترسی به صفحات", name: "pageAccess" },
                    ].map((item) => (
                      <div
                        key={item.name}
                        className="flex flex-row items-center justify-between gap-2"
                      >
                        {/* Label */}
                        <div className="flex flex-col items-start">
                          <label className="text-gray-600 text-sm">
                            {item.label}
                          </label>
                        </div>

                        {/* Multi Select */}
                        <div className="flex items-end">
                          <MultiSelectReactSelect
                            isDisabled={isOptionsLoading}
                            name={item.name}
                            value={selectedOptions[item.name]}
                            options={selectOptionsMap[item.name]}
                            onChange={handleSelectBoxChange}
                            className="w-[300px]"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </Disclosure.Panel>
              </>
            )}
          </Disclosure>
        </div>
      </div>

      <div className="my-4 flex items-center justify-center gap-x-6 ">
        <button
          disabled={loading}
          type="submit"
          className="w-full rounded-md bg-[#aadec5] px-10 py-2 text-sm font-semibold text-gray-600 shadow-xs hover:bg-[#93dcb8] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#aadec5]"
        >
          {loading ? <PulseLoader color="#f2f1f1" size={9} /> : "به روز رسانی "}
        </button>
      </div>
    </form>
  );
}
